/** @format */

import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { Chain, MyCustomizations, PersonalizationState } from '@shared/data-access/interfaces';
import {
	FavoritesActionDeterminationData,
	IAddProductRequest,
	IListName,
	IListShare,
	ILists,
	IOrchestratedListDetails,
	IOrchestratedLists,
	IPanelActions,
	IPanelBody,
	Product,
	ProductAttribute
} from '@shared/favorites/data-access/interfaces';
import { FavoritesActionResult, FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { Injectable, Signal, inject, signal } from '@angular/core';
import {
	MyAccountAnalyticsService,
	ProductDetailsTranslator,
	Store,
	WINDOW
} from '@shared/data-access/services';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';

import { AuthenticationService } from '@shared/authentication/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { WishListDao } from './wish-list.dao';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
	providedIn: 'root'
})
export class WishListService {
	private readonly analyticsService = inject(MyAccountAnalyticsService);
	private readonly wishListDao = inject(WishListDao);
	private readonly store = inject(Store);
	private readonly chain = inject(Chain);
	private readonly productDetailsTranslator = inject(ProductDetailsTranslator);
	private readonly authenticationService = inject(AuthenticationService);
	private readonly window = inject(WINDOW) as Window;

	shareListCount = new BehaviorSubject<number | null>(null);

	readonly actionInProgress = signal(false);

	/** LIST APIs */

	deleteWishlist$(listID: string) {
		// delete list from lists
		return this.wishListDao.deleteWishlist(listID).pipe(
			catchError((err) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_DELETE_LIST,
					'Failed to delete list.',
					err
				);
				throw new Error(err);
			})
		);
	}

	getLists$(offset: number = null, limit: number = null): Observable<ILists> {
		// get all the lists info
		return this.wishListDao
			.getLists(this.chain.chainMarketingIdentifierAbbr, offset, limit)
			.pipe(
				tap((response: ILists) => {
					let wishlistsInStorage = JSON.parse(localStorage.getItem('wishlists'));
					wishlistsInStorage = { lists: response };
					wishlistsInStorage.timestamp = new Date(
						new Date().getTime() + 3600000
					).getTime();
					localStorage.setItem('wishlists', JSON.stringify(wishlistsInStorage));
				})
			);
	}

	getOrchestratedLists$(offset: number = null, limit = 9): Observable<IOrchestratedLists> {
		return this.wishListDao
			.getOrchestratedLists(this.chain.chainMarketingIdentifierAbbr, offset, limit)
			.pipe(
				catchError((err) => {
					this.analyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_LISTS_DASHBOARD,
						'FailedToGetLists',
						err
					);
					return of(null);
				}),
				map((lists: IOrchestratedLists) => {
					if (!lists) {
						// TODO: change this to a constructor
						return {
							lists: [
								{
									recentProduct: {
										fullImageURL: '',
										sku: '',
										ecode: ''
									},
									productCount: 0,
									displayName: 'Favorites',
									id: '',
									isFull: false,
									brand: {
										brandID: this.chain.chainMarketingIdentifierAbbr
									},
									defaultList: true
								}
							],
							components: [],
							_metaData: {
								limit: 0,
								offset: 0,
								totalCount: 0
							}
						};
					}

					return lists;
				})
			);
	}

	// for public version of list of favoites to share with friends and family
	// we can copy the public list of favorites as it has access token.
	getPublicOrchestratedListDetails$(listIdOrToken: string, offset = 0, limit = 0) {
		return this.wishListDao.getPublicOrchestratedListDetails(listIdOrToken, offset, limit).pipe(
			map((listResponse: IOrchestratedListDetails) => {
				if (!listResponse.list.products) {
					listResponse.list.products = [];
				}
				listResponse.list.products = listResponse.list.products.map((product) => {
					product = this.productDetailsTranslator.toPricingTranslator(product);
					return product;
				});
				return listResponse;
			}),
			catchError((err) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.FAVORITES,
					`Failed to get orchestrated list details for list ${listIdOrToken}`,
					err
				);
				return throwError(() => err);
			})
		);
	}

	// for creating new athelete lists
	createOrUpdateList$(
		listName: string,
		listVisibilityParam?: boolean,
		listId?: string,
		listType = ''
	) {
		const listInfo: IListName = {
			name: listName,
			listVisibility: listVisibilityParam
		};

		if (listId) {
			listInfo.id = listId;
		}

		return this.wishListDao
			.createOrUpdateList(listInfo, listType, this.chain.chainMarketingIdentifierAbbr)
			.pipe(
				catchError((err: HttpErrorResponse) => {
					this.analyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_LISTS,
						err.error.message,
						err
					);
					return throwError(() => err);
				})
			);
	}

	/**
	 *
	 * @param isPublicList boolean
	 * @param listIdOrToken string
	 * @param offset number
	 * @param limit number
	 * @param forceRefreshList boolean
	 * @returns Orchestrated list details
	 */
	getOrchestratedListDetails$(
		isPublicList: boolean,
		listIdOrToken: string,
		offset = 0,
		limit = 0,
		isRecommended = false,
		listsEnabled = false
	) {
		let listsInStorage = JSON.parse(localStorage.getItem('favorites')) ?? {
			list: { products: [] }
		};
		if (isPublicList) {
			return this.getPublicOrchestratedListDetails$(listIdOrToken, offset, limit);
		}

		if (isRecommended) {
			const recommendedListInStorage = sessionStorage.getItem('recommended_list');

			if (recommendedListInStorage) {
				return of(JSON.parse(recommendedListInStorage));
			} else {
				return this.getRecommendedList$();
			}
		}

		return this.wishListDao
			.getOrchestratedListDetails(
				this.chain.chainMarketingIdentifierAbbr,
				listIdOrToken,
				offset,
				limit
			)
			.pipe(
				map((listResponse: IOrchestratedListDetails) => {
					// Safeguard for list engine not returning empty products property when it is expected to always be on lists that have been created but are empty.
					if (!listResponse.list.products) {
						listResponse.list.products = [];
					}

					listResponse.list.products = listResponse.list.products.map((product) => {
						product = this.productDetailsTranslator.toPricingTranslator(product);
						return product;
					});
					return listResponse;
				}),
				tap((listDetailsResponse) => {
					const storeKey = listsEnabled ? 'SAVEDPRODUCTS' : 'FAVORITES';
					listsInStorage = listDetailsResponse;
					this.store.set(storeKey, listsInStorage);
					localStorage.setItem('favorites', JSON.stringify(listsInStorage));
				}),
				catchError((err) => {
					this.analyticsService.generateLogError(
						MyAccountErrorLogKeys.FAVORITES,
						`Failed to get orchestrated list details for list ${listIdOrToken}`,
						err
					);
					return throwError(err);
				})
			);
	}

	/**
	 * @description returns the list details for a publicly shared list
	 * @param accessToken string
	 * @param offset string
	 * @param limit numbers
	 */
	getPublicListDetails$(accessToken: string, offset: number, limit: number) {
		return this.wishListDao.getPublicListDetails(accessToken, offset, limit).pipe(
			catchError((err) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.FAVORITES,
					'Failed to get shared list.',
					err
				);
				return throwError(err);
			})
		);
	}

	generateShareLink(listID: string): Observable<IListShare> {
		return this.wishListDao.generateShareLink(listID).pipe(
			catchError((err) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.FAVORITES,
					'Failed to generate share ID',
					err
				);
				return throwError(err);
			})
		);
	}

	/**
	 * @description returns list details for recommended list
	 */
	getRecommendedList$(): Observable<IOrchestratedListDetails> {
		const athleteID = JSON.parse(localStorage.getItem('TAG_IDs')).AthleteID.ID;
		const recommendedListInStorage = sessionStorage.getItem('recommended_list');

		if (!athleteID) {
			return of(null);
		}

		if (recommendedListInStorage) {
			return of(JSON.parse(recommendedListInStorage));
		}

		return this.wishListDao
			.getRecommendedList(athleteID, this.chain.chainMarketingIdentifierAbbr)
			.pipe(
				map((recommendedListResponse: IOrchestratedListDetails) => {
					recommendedListResponse.list.products =
						recommendedListResponse.list.products.map((product: Product) => {
							product = this.productDetailsTranslator.toPricingTranslator(product);
							return product;
						});
					return recommendedListResponse;
				}),
				tap((recommendedListResponse) => {
					sessionStorage.setItem(
						'recommended_list',
						JSON.stringify(recommendedListResponse)
					);
				}),
				catchError((err) => {
					this.analyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_LISTS_DASHBOARD,
						'Failed to get recommended list.',
						err
					);
					return throwError(err);
				})
			);
	}

	/** PRODUCT APIs */

	/**f
	 * @description Adds a product to a list
	 * @param listID: string. ListID product should be added to
	 * @param products: product[]. Array of products to add. Should only ever be a single item in array for web. Mobile will utilize the array.
	 * @returns
	 */

	addProductToList$(listID: string, products: IAddProductRequest, brand: string) {
		return this.wishListDao.addProductToList(listID, products, brand).pipe(
			tap((favoritesResponse: Product[]) => {
				// favorites should always exist but in case it doesn't (athlete deletes local storage while on the page), create empty favorites
				const favoritesInStorage = JSON.parse(localStorage.getItem('favorites')) ?? {
					list: { products: [] }
				};

				// if products array does not exist on the list, create it
				/* istanbul ignore next */
				if (!favoritesInStorage.list.products) {
					favoritesInStorage.list.products = [];
				}

				favoritesInStorage.list.products.push(favoritesResponse[0]);
				localStorage.setItem('favorites', JSON.stringify(favoritesInStorage));
				this.store.set('FAVORITES', favoritesInStorage);
			}),
			catchError((err) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.FAVORITES,
					`Failed to add item ${products.products[0].ecode} to list.`,
					err
				);
				return throwError(err);
			})
		);
	}

	deleteProductFromList$(productIds: string[]) {
		// delete list from lists
		return this.wishListDao.deleteProductInformation(productIds).pipe(
			tap(() => {
				/* istanbul ignore next */
				const favoritesInStorage = JSON.parse(localStorage.getItem('favorites')) ?? {
					list: { products: [] }
				};
				let itemIndex = -1;

				/* istanbul ignore next */
				productIds.forEach((productID) => {
					itemIndex = favoritesInStorage.list.products.findIndex(
						(item) => item.id === productID
					);
					if (itemIndex !== -1) {
						favoritesInStorage.list.products.splice(itemIndex, 1);
						favoritesInStorage.list.productCount =
							favoritesInStorage.list.productCount - 1;
					}
				});

				localStorage.setItem('favorites', JSON.stringify(favoritesInStorage));
				this.store.set('FAVORITES', favoritesInStorage);
				this.shareListCount.next(favoritesInStorage.list.productCount);
			}),
			catchError((err) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.FAVORITES,
					`Failed to delete item ${productIds[0]} from list.`,
					err
				);
				return throwError(err);
			})
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	getSavedItemsFromAllLists(ecode: string, sku?: string): Observable<any> {
		return this.wishListDao
			.getSavedItemsFromAllLists(ecode, this.chain.chainMarketingIdentifierAbbr)
			.pipe(
				tap((savedProducts) => {
					savedProducts = savedProducts
						? Object.entries(savedProducts).map(([key, value]) => ({
								key,
								value
							}))
						: [];
					this.store.set(
						'SAVEDPRODUCTS',
						new Map(savedProducts.map((obj) => [obj.key, obj.value]))
					);
				}),
				catchError((err) => {
					if (err?.status !== 499) {
						this.analyticsService.generateLogError(
							MyAccountErrorLogKeys.FAVORITES,
							'Failed to get mapBySku',
							err
						);
					}
					return throwError(err);
				})
			);
	}

	getFavoritedEcodes() {
		return this.wishListDao.getFavoritedEcodes(this.chain.chainMarketingIdentifierAbbr).pipe(
			tap((savedEcodes: any) => {
				const savedEcodesArray = savedEcodes
					? Object.entries(savedEcodes).map(([key, value]) => ({
							key,
							value
						}))
					: [];

				this.store.set(
					'SAVEDPRODUCTS',
					new Map(savedEcodesArray.map((obj) => [obj.key, obj.value]))
				);
			})
		);
	}

	/**
	 * @description Adds a product and the listIds the product was added too.
	 * @param skuOrEcode sku or ecode of product added to list(s)
	 * @param productIds array of productIds for the lists the product was added to
	 * @param currentProductsInStore the current state of saved products and the lists they belong too
	 */
	addProductToStore(
		skuOrEcode: string,
		productIds: string[],
		currentProductsInStore: Map<string, string[]>
	) {
		currentProductsInStore.set(skuOrEcode, productIds);

		this.store.set('SAVEDPRODUCTS', new Map(currentProductsInStore));
	}

	/**
	 * @description Adds a product and the listIds the product was deleted from.
	 * @param productId sku or ecode of product added to list(s)
	 * @param listIds array of listIds productId was added to
	 * @param currentProductsInStore the current state of saved products and the lists they belong too
	 */
	deleteProductFromStore(
		productId: string,
		listIds: string[],
		currentProductsInStore: Map<string, string[]>
	) {
		let productLists = currentProductsInStore.get(productId); // gets array of listIds product is currently in.
		productLists = productLists.filter((id) => !listIds.includes(id));

		if (!productLists.length) {
			delete currentProductsInStore[productId];
		} else {
			currentProductsInStore.set(productId, productLists);
		}

		this.store.set('SAVEDPRODUCTS', new Map(currentProductsInStore));
	}

	/**
	 * @param addOrRemoveReport IPanelActions; report of productIds for the lists it was added/removed from
	 * @param currentProductsInStore Map<string, string[]>; the current state of saved products and the lists they belong too
	 */
	manageProductsInStore(
		productKey: string,
		addOrRemoveReport: IPanelActions,
		currentProductsInStore: Map<string, string[]>
	) {
		// lists that the products are currently saved to
		let productLists = currentProductsInStore.get(productKey) ?? [];

		// add new productIds
		productLists = productLists.concat(addOrRemoveReport.added);

		// remove productIds
		productLists = productLists.filter((id) => !addOrRemoveReport.removed.includes(id));
		if (!productLists.length) {
			currentProductsInStore.delete(productKey);
		} else {
			currentProductsInStore.set(productKey, productLists);
		}

		this.store.set('SAVEDPRODUCTS', new Map(currentProductsInStore));
	}

	getProductAttributes(
		personalization: PersonalizationState | Record<string, never> = null,
		customAttributes: MyCustomizations = null
	): ProductAttribute {
		// product is personalizable
		if (personalization?.exists) {
			const attributes = Object.keys(personalization.personalization)
				.filter((key) => key.includes('x_personalizationTextLine'))
				.reduce((acc, key, index) => {
					if (typeof personalization.personalization[key] !== 'undefined') {
						const obj = {
							title: `Text Line ${index + 1}`,
							value: personalization.personalization[key]
						};
						acc.push(obj);
					}
					return acc;
				}, []);

			return { personalizedAttribute: attributes };
		} else if (customAttributes) {
			const attributes = Object.keys(customAttributes.MyCustomizerMap.data).reduce(
				(acc, key) => {
					const obj = {
						title: `${key}`,
						value: customAttributes.MyCustomizerMap.data[key]
					};
					acc.push(obj);
					return acc;
				},
				[]
			);

			return { customizerAttribute: attributes };
		}

		// Product is not custom or personalized. return empty object.
		return {};
	}

	/**
	 *
	 * @param resolvedSku: string
	 * @param personalizedAttributes: PersonalizationState | Record<string, never>
	 * @param customAttributes: MyCustomizations
	 * @returns Sku with customized/personalized stamp if product is customized or personalized
	 */
	determineSkuStamp(
		resolvedSku,
		personalizedAttributes: PersonalizationState | Record<string, never> = null,
		customAttributes: MyCustomizations = null
	): string {
		if (personalizedAttributes?.exists) {
			return `${resolvedSku}-P`;
		} else if (customAttributes) {
			return `${resolvedSku}-MC-${customAttributes.MyCustomizerMap.designId}`;
		}

		return resolvedSku;
	}

	removeSkuStamp(resolvedSku: string) {
		// remove stamped personalization or customization from list engine if it exists
		if (resolvedSku && resolvedSku.indexOf('-') !== -1) {
			return resolvedSku.substring(0, resolvedSku.indexOf('-'));
		}

		return resolvedSku || '';
	}

	// return the total count and current list count
	// takes chain as params
	getListCount() {
		return this.wishListDao.getListCount(this.chain.chainMarketingIdentifierAbbr).pipe(
			catchError((err) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_LIST_COUNT,
					'List has reached to max count.',
					err
				);
				return throwError(err);
			})
		);
	}
	getAllLists() {
		return this.wishListDao.getListCount(this.chain.chainMarketingIdentifierAbbr).pipe(
			catchError((err) => {
				this.analyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_GET_ALL_LISTS,
					'Failed to get the lists.',
					err
				);
				return throwError(err);
			})
		);
	}

	/* Shared favorites icon / button logic */
	productsInStoreSignal(): Signal<Map<string, string[]>> {
		return toSignal(
			this.store
				.select<Map<string, string[]>>('SAVEDPRODUCTS', true)
				.pipe(catchError(() => EMPTY)),
			{
				initialValue: new Map()
			}
		);
	}

	/** @description Used to set actionInProgress to true and back to false in case callback functions are not triggered */
	setInProgress() {
		this.actionInProgress.set(true);
		setTimeout(() => {
			this.actionInProgress.set(false);
		}, 2000);
	}

	determineFavoritesAction(
		data: FavoritesActionDeterminationData
	): Observable<FavoritesActionResult> {
		// If athlete is guest, automatically redirect them to Sign In
		if (!data.isAuthenticated) {
			this.analyticsService.triggerAnalyticsEvent('SignOut_AddToList');
			// Will handle redirect to sign in page, passing in query parameters if they exist (window.location.search)
			this.authenticationService
				.login({ target: location.pathname + location.search })
				.pipe(take(1))
				.subscribe();
			if (data.onWishlistBeacon) {
				data.onWishlistBeacon();
			}
			return of(FavoritesActionResult.TRIGGERED_LOGIN);
		}

		if (data.productIsFavorited) {
			this.setInProgress();
			// already in favorites so remove from list
			if (data.favoritesLocation === FavoritesLocationEnum.FAVORITES) {
				return this.deleteProductFromList$([data.favoritedProductId]).pipe(
					map(() => FavoritesActionResult.REMOVED),
					catchError(() => of(FavoritesActionResult.REMOVAL_ERROR)),
					finalize(() => {
						this.actionInProgress.set(false);
					})
				);
			}
			this.openListPanel(data);
			return of(FavoritesActionResult.OPENED_LIST_PANEL);
		}

		if (data.openQuickView) {
			this.setInProgress();
			// if on PLP or DS Recs, open react sidepanel to resolve sku
			this.window.quickview.openProductQuickView(data.ecode, data.quickViewOptions.referrer, {
				template: 'favorites',
				additionalData: data.quickViewOptions.additionalData,
				callback: () => {
					this.actionInProgress.set(false);
				}
			});
			if (data.onWishlistBeacon) {
				data.onWishlistBeacon();
			}
			return of(FavoritesActionResult.OPENED_QV_PANEL);
		}

		// Lock heart icon so can't be spammed
		this.setInProgress();
		this.openListPanel(data);
		return of(FavoritesActionResult.OPENED_LIST_PANEL);
	}

	openListPanel(data: FavoritesActionDeterminationData) {
		const panelBody: IPanelBody = {
			ecode: data.ecode
		};
		const panelAction = data.productIsFavorited ? 'remove-product' : 'add-product';

		// single sku products are saved at ecode so that it reflects properly on PLPs
		if (data.sku && !data.isSingleSkuProduct) {
			panelBody.sku = data.sku;
		}

		if (data.productAttributes) {
			panelBody.productAttributes = data.productAttributes;
		}

		this.analyticsService.triggerAnalyticsEvent('ModalOpen', { ModalName: 'ManageYourSaves' });

		this.window.headerFunctions.openListPanel(
			panelAction,
			panelBody,
			false,
			(panelListActions: IPanelActions) => {
				this.actionInProgress.set(false);
				const productKey = data.isSingleSkuProduct ? data.ecode : data.sku || data.ecode;
				this.manageProductsInStore(productKey, panelListActions, data.productsInStore);
			},
			data.favoritesLocation
		);
	}
}
